import React, { useMemo, useState } from 'react';
import { Search, Shuffle, Copy, ChevronLeft, ChevronRight, Flame } from 'lucide-react';
import { discordStyles, discordCategories } from '../data/styles';
import { DISCORD_T } from '../data/generatorsI18n';
import { applyCase } from '../lib/unicode';
import { copyText, textStats } from '../lib/helpers';
import { IMAGES } from '../data/mock';
import Faq from '../components/Faq';

export default function DiscordFontPage({ lang = 'en' }) {
  const L = DISCORD_T[lang];
  const [input, setInput] = useState('');
  const [caseMode, setCaseMode] = useState('none');
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);


  const text = applyCase(input || 'Discord', caseMode);
  const list = useMemo(() => discordStyles.filter((s) => (cat === 'All' || s.cat === cat) && s.name.toLowerCase().includes(q.toLowerCase())), [cat, q]);
  const cur = discordStyles[idx % discordStyles.length];
  const st = textStats(input);
  const copied = (name) => `${name} ${L.copiedSuffix}`;

  return (
    <div data-testid={`discord-page-${lang}`}>
      <section className="content-narrow pt-8 pb-5 text-center fade-up">
        <h1 className="font-hand text-[30px] md:text-[38px] leading-[1.35] text-slate-900" data-testid="page-title">{L.h1}</h1>
        <p className="text-[15px] text-slate-600 mt-2 max-w-[640px] mx-auto">{L.sub}</p>
      </section>

      <div className="max-w-[980px] mx-auto px-4">
        <div className="rounded-2xl bg-[var(--brand-soft)] border border-[var(--line)] p-4 md:p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <span className="text-[12px] text-slate-500"><b className="text-slate-800">{st.chars}</b> {L.chars} · <b className="text-slate-800">{st.words}</b> {L.words}</span>
            <div className="flex gap-1 bg-white rounded-lg p-1 border border-[var(--line)]">
              {L.cases.map(([k, l]) => (
                <button key={k} onClick={() => setCaseMode(k)} className={`px-2.5 py-1 text-[12px] rounded-md transition-colors ${caseMode === k ? 'bg-[var(--brand)] text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid={`discord-case-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={L.placeholder}
            className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-[16px] outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)]" data-testid="discord-input" />
          <div className="mt-3 flex items-center gap-2 bg-white rounded-xl border border-[var(--line)] px-3 py-2.5">
            <button className="p-1 rounded hover:bg-slate-100" onClick={() => setIdx((idx - 1 + discordStyles.length) % discordStyles.length)} data-testid="discord-prev"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex-1 text-[20px] text-slate-900 truncate text-center" data-testid="discord-current">{cur.fn(text)}</div>
            <button className="p-1 rounded hover:bg-slate-100" onClick={() => setIdx((idx + 1) % discordStyles.length)} data-testid="discord-next"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={() => copyText(cur.fn(text), copied(cur.name))} className="inline-flex items-center gap-1 bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white text-[13px] px-3 py-1.5 rounded-lg transition-colors" data-testid="discord-copy-current"><Copy className="w-3.5 h-3.5" />{L.copy}</button>
          </div>
          <div className="mt-3 rounded-xl bg-[#313338] text-white px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#5865f2] flex items-center justify-center font-bold">C</div>
            <div><div className="text-[15px] font-semibold">{cur.fn(input || L.sampleName)}</div><div className="text-[11px] text-[#949ba4]">{L.nickPreview}</div></div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-5">
          {discordCategories.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`px-3 py-1 text-[13px] rounded-full border transition-colors ${cat === c ? 'bg-[var(--brand)] border-[var(--brand)] text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-[var(--brand)]'}`} data-testid={`discord-cat-${c.toLowerCase()}`}>{L.cats[c]}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-[13px] text-slate-600"><b className="text-slate-900" data-testid="discord-count">{list.length}</b> {L.styles}</span>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L.search} className="w-full pl-9 pr-3 py-2 text-[13px] rounded-lg border border-slate-200 bg-white outline-none focus:border-[var(--brand)]" data-testid="discord-search" />
          </div>
          <button onClick={() => { const r = Math.floor(Math.random() * discordStyles.length); setIdx(r); copyText(discordStyles[r].fn(text), `${L.randomToast} ${copied(discordStyles[r].name)}`); }} className="inline-flex items-center gap-1 text-[13px] px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[var(--brand)]" data-testid="discord-random"><Shuffle className="w-3.5 h-3.5" />{L.random}</button>
          <button onClick={() => copyText(list.map((s) => s.fn(text)).join('\n'))} className="text-[13px] px-3 py-2 rounded-lg bg-[var(--brand)] text-white hover:bg-[var(--brand-hover)]" data-testid="discord-copy-all">{L.copyAll}</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 mt-4">
          {list.map((s) => (
            <button key={s.id} onClick={() => copyText(s.fn(text), copied(s.name))} className="discord-card text-left p-3.5" data-testid={`discord-style-${s.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11.5px] font-semibold text-slate-500">{s.name}</span>
                {s.hot && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#e5484d] bg-[#fdecec] px-1.5 py-0.5 rounded"><Flame className="w-3 h-3" />{L.hot}</span>}
              </div>
              <div className="text-[18px] text-slate-900 mt-1.5 truncate">{s.fn(text)}</div>
            </button>
          ))}
        </div>
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 grid grid-cols-2 md:grid-cols-6 gap-3 text-[13px]">
          {L.md.map(([a, b]) => (
            <div key={a}><b className="text-slate-800">{a}</b><div className="font-mono text-slate-500 text-[12px]">{b}</div></div>
          ))}
        </div>
      </div>

      <section className="content-narrow py-12 prose-site">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="!mt-0">{L.whatTitle}</h2>
            <p>{L.what}</p>
          </div>
          <img src={IMAGES.gamer} alt={L.imgAlt} className="rounded-xl shadow-lg w-full h-[240px] object-cover" loading="lazy" />
        </div>
        <h2>{L.howTitle}</h2>
        <ul>{L.how.map(([b, d]) => <li key={b}><b>{b}</b> {d}</li>)}</ul>
        <h2>{L.tipsTitle}</h2>
        <ul>{L.tips.map((t) => <li key={t}>{t}</li>)}</ul>
      </section>
      <Faq items={L.faq} title={L.faqTitle} />
    </div>
  );
}
